import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './core/theme/theme';
import { AuthProvider } from './core/auth/AuthProvider';
import { TimerProvider } from './features/kronos/timer/TimerContext';
import { RequireAuth } from './core/auth/RequireAuth';
import { RequireAdmin } from './core/auth/RequireAdmin';
import { RequireRole } from './core/auth/RequireRole';
import DevUserSwitcher from './core/dev/DevUserSwitcher';
import AppShell from './layout/AppShell';
import LoginPage from './pages/LoginPage';
import ProjectsHub from './pages/ProjectsHub';
import ProjectDetail from './pages/ProjectDetail';
import ProjectLifePage from './pages/ProjectLifePage';
import ProjectOverview from './features/projects/ProjectOverview';
import FinanceTable from './features/projects/FinanceTable';
import ProjectKronosTab from './features/projects/ProjectKronosTab';
import ProjectMilestonesTab from './features/projects/ProjectMilestonesTab';
import ProjectAvantProjetTab from './features/projects/ProjectAvantProjetTab';
import TimeTracker from './pages/TimeTracker';
import KronosDashboard from './pages/KronosDashboard';
import TimesheetView from './pages/TimesheetView';
import CalendarView from './features/kronos/calendar/CalendarView';
import NotificationsPage from './pages/NotificationsPage';
import AdminSettings from './pages/AdminSettings';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import KronosReport from './pages/KronosReport';
import NotFound from './pages/NotFound';

const emotionCache = createCache({ key: 'css', prepend: true });

export default function App() {
  return (
    <CacheProvider value={emotionCache}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <TimerProvider>
          {import.meta.env.VITE_USE_EMULATORS === 'true' && <DevUserSwitcher />}
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/projects" replace />} />
              <Route path="projects" element={<ProjectsHub />} />
              <Route path="projects/:id" element={<ProjectDetail />}>
                <Route index element={<ProjectOverview />} />
                <Route path="finance" element={<FinanceTable />} />
                <Route path="kronos" element={<ProjectKronosTab />} />
                <Route path="milestones" element={<ProjectMilestonesTab />} />
                <Route path="avant-projet" element={<ProjectAvantProjetTab />} />
              </Route>
              {/* Même audience que l'entrée de menu (Sidebar.tsx:62) : la barre
                  latérale masquait l'écran sans que la route le ferme, donc une URL
                  tapée l'ouvrait quand même. Les jalons, eux, sont déjà filtrés côté
                  serveur (handler.ts:192) — ce qui s'affichait alors était un tableau
                  de tous les projets actifs avec une conformité vide, donc faux. */}
              <Route
                path="project-life"
                element={
                  <RequireRole allowedRoles={['admin', 'direction']}>
                    <ProjectLifePage />
                  </RequireRole>
                }
              />
              <Route path="kronos" element={<Navigate to="/kronos/tracker" replace />} />
              <Route path="kronos/tracker" element={<TimeTracker />} />
              <Route
                path="kronos/dashboard"
                element={<KronosDashboard />}
              />
              <Route
                path="kronos/report"
                element={<KronosReport />}
              />
              <Route path="kronos/timesheet" element={<TimesheetView />} />
              <Route path="kronos/calendar" element={<CalendarView />} />
              <Route path="notifications" element={<NotificationsPage />} />
              {/* Customers — read-only for everyone, write gated by Firestore rules + UI. */}
              <Route path="customers" element={<ClientsPage />} />
              <Route path="customers/:id" element={<ClientDetailPage />} />
              <Route
                path="settings"
                element={
                  <RequireAdmin>
                    <AdminSettings />
                  </RequireAdmin>
                }
              />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          </TimerProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
    </CacheProvider>
  );
}
